import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { MdArrowBack } from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import * as apicall from '../../components/Apicall';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { FaChevronDown } from "react-icons/fa6";
import DatePicker from 'react-datepicker';
import { IoCalendarOutline } from "react-icons/io5";


const AddUsers = () => {
  const navigate = useNavigate();
  const [specializations, setSpecializations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [fileName, setFileName] = useState('');
  const [profileImageBase64, setProfileImageBase64] = useState('');
  const [loading, setLoading] = useState(false);
  const [vendorServices, setVendorServices] = useState([]);
  const [vendorNames, setVendorNames] = useState([]);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [vendorFeeRange, setVendorFeeRange] = useState({ from: '', to: '' });
  const [vendorWorkingHours, setVendorWorkingHours] = useState({ start: '', end: '' });
  const [educationOptions, setEducationOptions] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [vendorPage, setVendorPage] = useState(1);
  const [hasMoreVendors, setHasMoreVendors] = useState(true);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [savedValues, setSavedValues] = useState(null);
  const VENDORS_PER_PAGE = 30;
  const vendorDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const ROLE_IDS = {
    ADMIN: 1,
    CUSTOMER_SUPPORT: 2,
    VENDOR: 3,
    END_USER: 4,
    VENDOR_ADMIN: 5,
    SERVICE_PROVIDER: 6,
  };

  // Add function to check if role needs availability setup
  const needsAvailabilitySetup = (roleId) => {
    return [ROLE_IDS.VENDOR_ADMIN, ROLE_IDS.SERVICE_PROVIDER].includes(parseInt(roleId));
  };

  useEffect(() => {
    fetchRoles();
    fetchServices();
    const savedUserDetails = sessionStorage.getItem('doctorDetails');
    if (savedUserDetails) {
      const parsedDetails = JSON.parse(savedUserDetails);

      // Set education options from session if available
      if (parsedDetails.educationOptionsList && parsedDetails.educationOptionsList.length > 0) {
        setEducationOptions(parsedDetails.educationOptionsList);
      }
      // Set services from session if available
      if (parsedDetails.servicesList && parsedDetails.servicesList.length > 0) {
        setServices(parsedDetails.servicesList);
      }
      // Set vendor names
      if (parsedDetails.vendor_name) {
        setVendorNames([{
          id: parsedDetails.vendor_id,
          name: parsedDetails.vendor_name,
          fee_from: parsedDetails.fee_from,
          fee_to: parsedDetails.fee_to,
          working_hours_start: parsedDetails.working_hours_start,
          working_hours_end: parsedDetails.working_hours_end,
          service: parsedDetails.service,
          service_name: parsedDetails.service_name
        }]);
      }

      const formattedData = {
        doctorName: parsedDetails.doctorName || '',
        emailAddress: parsedDetails.email || '',
        mobileNumber: parsedDetails.phone_number || '',
        gender: parsedDetails.gender === 1 ? 'male' : parsedDetails.gender === 2 ? 'female' : '',
        education: parsedDetails.education || '',
        specialization: parsedDetails.specialization?.toString() || '',
        experienceStartDate: parsedDetails.practice_start || '',
        consultationFees: parsedDetails.fee?.toString() || '',
        role: parsedDetails.role?.toString() || ROLE_IDS.VENDOR_ADMIN.toString(),
        dateOfBirth: parsedDetails.dob || '',
        about: parsedDetails.about_user || '',
        uniqueId: parsedDetails.unique_id || '',
        requiredLogin: parsedDetails.is_login_eligible ? 'Yes' : 'No',
        selectedVendor: parsedDetails.vendor_id || '',
        selectedService: parsedDetails.service || '',
      };

      // Set vendor fee range and working hours
      if (parsedDetails.fee_from && parsedDetails.fee_to) {
        setVendorFeeRange({
          from: parsedDetails.fee_from,
          to: parsedDetails.fee_to
        });
      }

      if (parsedDetails.working_hours_start && parsedDetails.working_hours_end) {
        setVendorWorkingHours({
          start: parsedDetails.working_hours_start,
          end: parsedDetails.working_hours_end
        });
      }

      setSavedValues(formattedData);

      // If there's a service, trigger the service change handler
      if (parsedDetails.service) {
        fetchVendorServices(parsedDetails.service, 1, true, '');
        fetchServiceOfferings(parsedDetails.service);
        fetchEducationOptions(parsedDetails.service);
      }

      if (parsedDetails.profile_image) {
        setProfileImageBase64(parsedDetails.profile_image);
        setFileName('Profile image uploaded');
      }
    }
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await apicall.get(`roles`);
      if (response && response.data) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };


  const fetchServices = async () => {
    try {
      const response = await apicall.get('services?category_id=2');
      if (response && response.data) {
        const savedUserDetails = sessionStorage.getItem('doctorDetails');
        if (savedUserDetails) {
          const parsedDetails = JSON.parse(savedUserDetails);
          if (parsedDetails.service && parsedDetails.service_name) {
            // Filter out any service that matches either the ID or name from session storage
            const filteredServices = response.data.filter(service =>
              String(service.id) !== String(parsedDetails.service) &&
              service.name.toLowerCase() !== parsedDetails.service_name.toLowerCase()
            );

            // Add the saved service at the beginning of the array
            setServices([
              {
                id: parsedDetails.service,
                name: parsedDetails.service_name
              },
              ...filteredServices
            ]);
          } else {
            setServices(response.data);
          }
        } else {
          setServices(response.data);
        }
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };


  const fetchVendorServices = async (serviceId, page = 1, isInitialLoad = false, searchTerm = '') => {
    try {
      setIsLoadingVendors(true);
      const response = await apicall.get(
        `get_vendor_details?service=${serviceId}&page=${page}&limit=${VENDORS_PER_PAGE}&status=1&name=${searchTerm}`
      );
      if (response && response.data) {
        const savedUserDetails = sessionStorage.getItem('doctorDetails');
        let vendors = response.data.map(vendor => ({
          id: vendor.vendor_id,
          name: `${vendor.first_name} ${vendor.last_name}`.trim(),
          fee_from: vendor.fee_from || '',
          fee_to: vendor.fee_to || '',
          working_hours_start: vendor.working_hours_start || vendor.working_hours_from || '',
          working_hours_end: vendor.working_hours_end || vendor.working_hours_to || '',
          service: vendor.service,
          service_name: vendor.service_name
        }));

        if (savedUserDetails && isInitialLoad) {
          const parsedDetails = JSON.parse(savedUserDetails);
          if (
            parsedDetails.vendor_id &&
            parsedDetails.vendor_name &&
            String(parsedDetails.service) === String(serviceId)
          ) {
            // Remove any vendor that matches the saved vendor's ID or name
            vendors = vendors.filter(
              vendor =>
                String(vendor.id) !== String(parsedDetails.vendor_id) &&
                vendor.name.toLowerCase().trim() !== parsedDetails.vendor_name.toLowerCase().trim()
            );

            // Add saved vendor at the beginning
            vendors.unshift({
              id: parsedDetails.vendor_id,
              name: parsedDetails.vendor_name,
              fee_from: parsedDetails.fee_from || '',
              fee_to: parsedDetails.fee_to || '',
              working_hours_start: parsedDetails.working_hours_start || '',
              working_hours_end: parsedDetails.working_hours_end || '',
              service: parsedDetails.service,
              service_name: parsedDetails.service_name
            });
          }
        }

        // Remove any remaining duplicates based on ID
        vendors = vendors.filter((vendor, index, self) =>
          index === self.findIndex(v => String(v.id) === String(vendor.id))
        );

        setVendorNames(prevVendors => {
          if (isInitialLoad) {
            return vendors;
          } else {
            // For pagination, ensure no duplicates when adding new vendors
            const existingIds = new Set(prevVendors.map(v => String(v.id)));
            const newVendors = vendors.filter(v => !existingIds.has(String(v.id)));
            return [...prevVendors, ...newVendors];
          }
        });

        setHasMoreVendors(vendors.length === VENDORS_PER_PAGE);
        setVendorPage(page);
      }
    } catch (error) {
      console.error('Error fetching vendor services:', error);
    } finally {
      setIsLoadingVendors(false);
    }
  };



  const fetchServiceOfferings = async (serviceId) => {
    try {
      const response = await apicall.get(`get_service_offerings/${serviceId}`);
      if (response && response.data) {
        setSpecializations(response.data);
      }
    } catch (error) {
      console.error('Error fetching specializations:', error);
    }
  };

  const fetchEducationOptions = async (serviceId) => {
    try {
      const response = await apicall.get(`serviceProviderEducation/${serviceId}`);
      if (response && response.data) {
        const savedUserDetails = sessionStorage.getItem('doctorDetails');
        if (savedUserDetails) {
          const parsedDetails = JSON.parse(savedUserDetails);
          if (parsedDetails.education && parsedDetails.education_name) {
            // Filter out any education that matches either the ID or name from session storage
            const filteredEducation = response.data.filter(edu =>
              String(edu.id) !== String(parsedDetails.education) &&
              edu.degree_name.toLowerCase() !== parsedDetails.education_name.toLowerCase()
            );

            // Add the saved education at the beginning of the array
            setEducationOptions([
              {
                id: parsedDetails.education,
                degree_name: parsedDetails.education_name
              },
              ...filteredEducation
            ]);
          } else {
            setEducationOptions(response.data);
          }
        } else {
          setEducationOptions(response.data);
        }
      }
    } catch (error) {
      console.error('Error fetching education options:', error);
    }
  };

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value;
    setVendorSearchTerm(searchTerm);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }


    const timeoutId = setTimeout(() => {
      setVendorPage(1);
      setVendorNames([]);
      fetchVendorServices(selectedService, 1, true, searchTerm);
    }, 500);
    setSearchTimeout(timeoutId);
  };

  const handleVendorScroll = useCallback((e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && !isLoadingVendors && hasMoreVendors) {
      fetchVendorServices(selectedService, vendorPage + 1, false, vendorSearchTerm);
    }
  }, [vendorPage, isLoadingVendors, hasMoreVendors, selectedService, vendorSearchTerm]);

  const handleServiceChange = async (serviceId, setFieldValue) => {
    setSelectedService(serviceId);
    setFieldValue('selectedService', serviceId);
    setFieldValue('selectedVendor', '');
    setVendorPage(1);
    setHasMoreVendors(true);
    setVendorSearchTerm('');

    await Promise.all([
      fetchVendorServices(serviceId, 1, true, ''),
      fetchServiceOfferings(serviceId),
      fetchEducationOptions(serviceId)
    ]);
  };

  const checkUserExists = async (field, value, role) => {
    try {
      const data = {
        role: role,
        [field]: value
      };
      const response = await apicall.post(data, 'checkUserExists');
      return response.data;
    } catch (error) {
      console.error('Error checking user existence:', error);
      return null;
    }
  };

  const initialValues = {
    doctorName: '',
    emailAddress: '',
    mobileNumber: '',
    gender: '',
    education: '',
    specialization: '',
    experienceStartDate: '',
    consultationFees: '',
    role: ROLE_IDS.VENDOR_ADMIN.toString(),
    vendorService: '',
    address: '',
    city: '',
    state: '',
    country: '',
    dateOfBirth: '',
    about: '',
    uniqueId: '',
    requiredLogin: '',
    profileImage: '',
    selectedVendor: '',
    selectedService: '',
    vendor_name: ''
  };

  const validationSchema = Yup.object().shape({
    doctorName: Yup.string()
      .required('Doctor name is required')
      .min(2, 'Doctor name must be at least 2 characters')
      .max(20, 'Doctor name cannot exceed 20 characters'),
    gender: Yup.string()
      .required('Gender is required'),

    dateOfBirth: Yup.date()
      .required('Date of birth is required')
      .max(new Date(), 'Date of birth cannot be in the future')
      .test(
        'min-age',
        'You must be at least 15 years old',
        function (value) {
          if (!value) return true;
          const today = new Date();
          const fifteenYearsAgo = new Date(today.setFullYear(today.getFullYear() - 15));
          return new Date(value) <= fifteenYearsAgo;
        }
      ),
    experienceStartDate: Yup.date()
      .required('Experience start date is required')
      .max(new Date(), 'Experience start date cannot be in the future')
      .test(
        'experience-after-dob',
        'Experience start date must be after date of birth',
        function (value) {
          const { dateOfBirth } = this.parent;
          if (!value || !dateOfBirth) return true;
          return new Date(value) > new Date(dateOfBirth);
        }
      ),

    mobileNumber: Yup.string()
      .required('Contact number is required')
      .matches(/^[0-9]+$/, 'Contact number must be numeric')
      .test('country-specific-length', 'Invalid phone number length', function (value) {
        if (!value) return true;
        const cleanedValue = value.replace(/\D/g, '');
        if (cleanedValue.startsWith('91')) {
          if (cleanedValue.length < 12) {
            return this.createError({ message: 'Please enter a valid Indian phone number (min 10 digits)' });
          }
          return true;
        }
        if (cleanedValue.startsWith('971')) {
          if (cleanedValue.length < 12) {
            return this.createError({ message: 'Please enter a valid UAE phone number (min 9 digits)' });
          }
          return true;
        }
        // Default: minimum 10 digits (including country code)
        if (cleanedValue.length < 10) {
          return this.createError({ message: 'Please enter a valid phone number (min 8 digits)' });
        }
        return true;
      })
      .test('unique-phone', 'This phone number is already registered', async function (value) {
        if (!value) return true;
        const result = await checkUserExists('phone_number', value, ROLE_IDS.VENDOR_ADMIN);
        return !result?.exists;
      }),
    emailAddress: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'Invalid email format'
      )
      .required('Email is required')
      .test('unique-email', 'This email is already registered', async function (value) {
        if (!value) return true;
        const result = await checkUserExists('email', value, ROLE_IDS.VENDOR_ADMIN);
        return !result?.exists;
      }),
    about: Yup.string()
      .required('About section is required')
      .min(10, 'About section must be at least 10 characters')
      .max(500, 'About section cannot exceed 500 characters'),
    consultationFees: Yup.number()
      .required('Consultation fees is required')
      .min(0, 'Consultation fees must be positive')
      .test('fee-range', 'Fee must be within vendor\'s allowed range', function (value) {
        if (!value) return true;
        const selectedVendor = vendorNames.find(v => v.id === this.parent.selectedVendor);
        if (!selectedVendor) return true;

        const fee = parseInt(value);
        const from = parseInt(selectedVendor.fee_from) || 0;
        const to = parseInt(selectedVendor.fee_to) || Infinity;

        return fee >= from && fee <= to;
      }),
    uniqueId: Yup.string()
      .required('Unique ID is required')
      .min(3, 'Unique ID must be at least 3 characters')
      .max(20, 'Unique ID cannot exceed 20 characters'),
    role: Yup.string()
      .required('Role is required'),
    education: Yup.string()
      .required('Education is required'),
    specialization: Yup.string()
      .required('Specialization is required'),
    selectedService: Yup.string()
      .required('Service is required'),
    selectedVendor: Yup.string()
      .required('Vendor is required'),
  });

  const IMAGE_SIZE_LIMIT = 500 * 1024;
  const IMAGE_DIMENSIONS_WIDTH = 400;
  const IMAGE_DIMENSIONS_HEIGHT = 220;

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > IMAGE_SIZE_LIMIT) {
        toast.error('Image file is too large (max 500 KB)');
        return;
      }
      const img = new window.Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width > IMAGE_DIMENSIONS_WIDTH || img.height > IMAGE_DIMENSIONS_HEIGHT) {
          toast.error('Image dimensions are too large (max 400w x 220h)');
          return;
        }
        setFileName(file.name);
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result;
          setProfileImageBase64(base64String);
        };
        reader.readAsDataURL(file);
      };
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const nameParts = values.doctorName.trim().split(' ');
      const selectedEducation = educationOptions.find(e => e.id === values.education);
      const selectedServiceObj = services.find(s => s.id === values.selectedService);
      const selectedVendorObj = vendorNames.find(v => v.id === values.selectedVendor);

      const data = {
        role: values.role,
        doctorName: values.doctorName,
        first_name: nameParts[0] || '',
        middle_name: nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '',
        last_name: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
        email: values.emailAddress,
        phone_number: values.mobileNumber,
        gender: values.gender ? (values.gender === 'male' ? 1 : 2) : null,
        education: values.education || '',
        education_name: selectedEducation ? selectedEducation.degree_name : '',
        practice_start: values.experienceStartDate,
        specialization: parseInt(values.specialization),
        country: values.country || 1,
        state: values.state || 1,
        city: values.city || 1,
        about_user: values.about,
        vendor_id: values.selectedVendor || '',
        vendor_name: selectedVendorObj ? selectedVendorObj.name : '',
        address: values.address || '',
        is_active: 1,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        dob: values.dateOfBirth,
        created_by: 300,
        is_login_eligible: values.requiredLogin === "Yes" ? true : false,
        profile_image: profileImageBase64,
        fee: parseInt(values.consultationFees) || 0,
        unique_id: values.uniqueId || '',
        working_hours_start: vendorWorkingHours.start,
        working_hours_end: vendorWorkingHours.end,
        fee_from: vendorFeeRange.from,
        fee_to: vendorFeeRange.to,
        service: values.selectedService,
        service_name: selectedServiceObj ? selectedServiceObj.name : '',
      };


      if (needsAvailabilitySetup(values.role)) {
        sessionStorage.setItem('doctorDetails', JSON.stringify(data));
        navigate('/Users/add/availability');
      } else {
        const res = await apicall.post(data, 'service_providers_register');
        if (res && (res.status === 200 || (res.message && res.message.includes('successfully')))) {
          toast.success(res.message || 'User added successfully');
          setTimeout(() => {
            navigate('/Users');
          }, 1500);
          return;
        }
        throw new Error(res?.message || 'Failed to add user');
      }
    } catch (e) {
      console.error('Error in handleSubmit:', e);
      if (!e.message.includes('successfully')) {
        toast.error(e.message.replace('Error:', ''));
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleVendorSelect = (vendorId) => {
    const selectedVendor = vendorNames.find(v => v.id === vendorId);

    if (selectedVendor) {
      setVendorFeeRange({
        from: selectedVendor.fee_from || '',
        to: selectedVendor.fee_to || ''
      });

      const workingHours = {
        start: selectedVendor.working_hours_start || selectedVendor.working_hours_from || '09:00',
        end: selectedVendor.working_hours_end || selectedVendor.working_hours_to || '17:00'
      };

      setVendorWorkingHours(workingHours);
    }
  };

  const filteredVendors = vendorNames;

  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname.toLowerCase();
      if (!currentPath.includes('/users/add') && !currentPath.includes('/users/profile/')) {
        sessionStorage.removeItem('doctorDetails');
      }
    };
  }, []);

  const handleNavigation = () => {
    if (window.location.pathname.toLowerCase() === '/users/add') {
      sessionStorage.removeItem('doctorDetails');
    }
    navigate('/Users');
  };

  return (
    <div className="px-3">
      <div className="d-flex align-items-center mb-3 pt-3">
        <Button
          variant="link"
          className="text-dark text-decoration-none p-0 d-flex align-items-center back-button"
          onClick={handleNavigation}
        >
          <MdArrowBack size={20} /> BACK
        </Button>
      </div>

      <div className="form-container">
        <h4 className="mb-4 page-heading">Add Users</h4>

        <Formik
          initialValues={savedValues || initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            setFieldError,
          }) => (
            <Form onSubmit={handleSubmit}>
              {/* Personal Details Section */}
              <div className="mb-4">
                <h6 className="mb-4 section-heading">Personal Details</h6>

                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Role</Form.Label>
                      <div className="position-relative">
                        <div
                          className={`form-input d-flex justify-content-between align-items-center ${values.role === "" ? "placeholder-style" : ""}`}
                          onClick={() => {
                            const select = document.getElementById('roleSelect');
                            select.click();
                          }}
                        >
                          <span>
                            {values.role
                              ? roles.find(role => role.id === parseInt(values.role))?.name
                              : 'Select Role'}
                          </span>
                          <span className="ms-2"><FaChevronDown /></span>
                        </div>
                        <Form.Select
                          id="roleSelect"
                          name="role"
                          value={values.role}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.role && errors.role}
                          className="position-absolute form-input bg-white"
                          style={{ height: '40px', width: '100%', top: 0, left: 0 }}
                          disabled
                        >
                          <option value="">Select Role</option>
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </Form.Select>
                        {touched.role && errors.role && (
                          <Form.Control.Feedback type="invalid">
                            {errors.role}
                          </Form.Control.Feedback>
                        )}
                      </div>
                    </Form.Group>
                  </Col>


                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Service</Form.Label>
                      <Form.Select
                        name="selectedService"
                        value={values.selectedService}
                        onChange={e => handleServiceChange(e.target.value, setFieldValue)}
                        onBlur={handleBlur}
                        isInvalid={touched.selectedService && errors.selectedService}
                        className={`form-input ${values.selectedService === "" ? "placeholder-style" : ""}`}
                      >
                        <option value="">Select Service</option>
                        {services.map(service => (
                          <option key={service.id} value={service.id}>{service.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.selectedService}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Select Vendor</Form.Label>
                      <div className="position-relative" ref={vendorDropdownRef}>
                        <div
                          className={`form-control d-flex justify-content-between align-items-center 
                          ${!values.selectedService ? 'bg-light' : ''}
                          ${touched.selectedVendor && errors.selectedVendor ? 'is-invalid' : ''}`}
                          onClick={async () => {
                            if (values.selectedService) {
                              if (!showVendorDropdown) {
                                // Dropdown is being opened
                                setShowVendorDropdown(true); // Open immediately
                                if (!vendorSearchTerm) {
                                  setVendorNames([]); // Optionally clear current list to avoid flicker
                                  fetchVendorServices(values.selectedService, 1, true, '');
                                }
                              } else {
                                // Dropdown is being closed
                                setVendorSearchTerm('');
                                setShowVendorDropdown(false);
                              }
                            }
                          }}
                        >
                          <span style={{ color: values.selectedVendor ? '#000' : '#898989' }}>
                            {values.selectedVendor
                              ? vendorNames.find(v => v.id === values.selectedVendor)?.name
                              : 'Select vendor'}

                          </span>
                          <span className="ms-2 text-dark"><FaChevronDown /></span>
                        </div>

                        {showVendorDropdown && values.selectedService && (
                          <div
                            className="position-absolute w-100 bg-white border rounded mt-1"
                            style={{
                              maxHeight: '300px',
                              zIndex: 1000,
                            }}
                          >
                            <div className="sticky-top bg-white border-bottom p-2">
                              <Form.Control
                                type="text"
                                placeholder="Search vendor..."
                                value={vendorSearchTerm}
                                onChange={handleSearchChange}
                                className="form-input secondary-text"
                                style={{ height: '35px', fontSize: '14px' }}
                                onClick={(e) => e.stopPropagation()}
                                disabled={!values.selectedService}
                              />
                            </div>
                            <div
                              style={{ maxHeight: '240px', overflowY: 'auto' }}
                              onScroll={handleVendorScroll}
                            >
                              {filteredVendors.length > 0 ? (
                                <>
                                  {filteredVendors.map((vendor) => (
                                    <div
                                      key={vendor.id}
                                      className="px-3 py-2 cursor-pointer hover-bg-light"
                                      style={{ cursor: 'pointer', fontSize: '12px' }}
                                      onClick={() => {
                                        setFieldValue('selectedVendor', vendor.id);
                                        handleVendorSelect(vendor.id);
                                        setVendorSearchTerm('');
                                        setShowVendorDropdown(false);
                                      }}
                                      onMouseEnter={(e) =>
                                        e.currentTarget.style.backgroundColor = '#f1f3f5'
                                      }
                                      onMouseLeave={(e) =>
                                        e.currentTarget.style.backgroundColor = 'white'
                                      }
                                    >
                                      {vendor.name}
                                    </div>
                                  ))}
                                  {isLoadingVendors && (
                                    <div className="text-center py-2">
                                      <Spinner animation="border" size="sm" />
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="px-3 py-2 text-muted">
                                  {isLoadingVendors ? 'loading...' : 'No vendors found'}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <Form.Control
                          type="hidden"
                          name="selectedVendor"
                          value={values.selectedVendor}
                          isInvalid={touched.selectedVendor && errors.selectedVendor}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.selectedVendor}
                        </Form.Control.Feedback>
                      </div>
                    </Form.Group>
                  </Col>


                </Row>

                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Doctor Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="doctorName"
                        placeholder="Enter doctor name"
                        value={values.doctorName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.doctorName && errors.doctorName}
                        className="form-input"
                      />
                      {touched.doctorName && errors.doctorName && (
                        <Form.Control.Feedback type="invalid">{errors.doctorName}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Gender</Form.Label>
                      <Form.Select
                        name="gender"
                        value={values.gender}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.gender && errors.gender}
                        className={`form-input ${values.gender === "" ? "placeholder-style" : ""}`}
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </Form.Select>
                      {touched.gender && errors.gender && (
                        <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Education</Form.Label>
                      <Form.Select
                        name="education"
                        value={values.education}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.education && errors.education}
                        className={`form-input ${values.education === "" ? "placeholder-style" : ""}`}

                      >
                        <option value="">Select Education</option>
                        {educationOptions.map((edu) => (
                          <option key={edu.id} value={edu.id}>{edu.degree_name}</option>
                        ))}
                      </Form.Select>
                      {touched.education && errors.education && (
                        <Form.Control.Feedback type="invalid">{errors.education}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Specialization</Form.Label>
                      <Form.Select
                        name="specialization"
                        value={values.specialization}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.specialization && errors.specialization}
                        className={` ${values.specialization === "" ? "placeholder-style" : ""}`}
                        style={{ fontSize: '14px' }}                      >
                        <option value="">Select Specialization</option>
                        {specializations.map((spec) => (
                          <option key={spec.id} value={spec.id}>
                            {spec.name}
                          </option>
                        ))}
                      </Form.Select>
                      {touched.specialization && errors.specialization && (
                        <Form.Control.Feedback type="invalid">
                          {errors.specialization}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Date of Birth</Form.Label>
                      <div className="position-relative">
                        <DatePicker
                          selected={values.dateOfBirth ? new Date(values.dateOfBirth) : null}
                          onChange={(date) => setFieldValue('dateOfBirth', date)}
                          onBlur={() => setFieldTouched('dateOfBirth', true)}
                          dateFormat="yyyy-MM-dd"
                          maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 15) - 24 * 60 * 60 * 1000)} // Minimum age 15 years, up to yesterday
                          placeholderText="Select date of birth"
                          className={`form-control ${errors.dateOfBirth && touched.dateOfBirth ? 'is-invalid' : ''}`}
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={100}
                          dropdownMode="select"
                          peekNextMonth
                          showMonthDropdown
                        />
                        <IoCalendarOutline
                          className="position-absolute end-1 top-50 translate-middle-y text-secondary text-dark"
                          style={{
                            right: errors.dateOfBirth && touched.dateOfBirth ? '1.75rem' : '0.75rem',
                            pointerEvents: 'none' // Makes the icon non-interactive
                          }}
                        />
                        {errors.dateOfBirth && touched.dateOfBirth && (
                          <div
                            className="invalid-feedback d-block"
                            style={{
                              position: 'absolute',
                              bottom: '-1.25rem',
                              left: 0,
                              fontSize: '0.875em',
                              color: '#dc3545'
                            }}
                          >
                            {errors.dateOfBirth}
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Experience Started On</Form.Label>
                      <div className="position-relative">
                        <DatePicker
                          selected={values.experienceStartDate ? new Date(values.experienceStartDate) : null}
                          onChange={(date) => setFieldValue('experienceStartDate', date)}
                          onBlur={() => setFieldTouched('experienceStartDate', true)}
                          dateFormat="yyyy-MM-dd"
                          maxDate={new Date(Date.now() - 24 * 60 * 60 * 1000)}
                          placeholderText="Select start date"
                          className={`form-control ${errors.experienceStartDate && touched.experienceStartDate ? 'is-invalid' : ''}`}
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={100}
                          dropdownMode="select"
                          peekNextMonth
                          showMonthDropdown
                        />
                        <IoCalendarOutline
                          className="position-absolute end-1 top-50 translate-middle-y text-secondary pe-none text-dark"
                          style={{
                            right: errors.experienceStartDate && touched.experienceStartDate ? '1.75rem' : '0.75rem'
                          }}
                        />
                        {errors.experienceStartDate && touched.experienceStartDate && (
                          <div
                            className="invalid-feedback d-block"
                            style={{
                              position: 'absolute',
                              bottom: '-1.25rem',
                              left: 0,
                              fontSize: '0.875em',
                              color: '#dc3545'
                            }}
                          >
                            {errors.experienceStartDate}
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <h6 className="mb-4 section-heading">About You</h6>
                  <Form.Control
                    as="textarea"
                    name="about"
                    rows={3}
                    placeholder="Small introduction about you"
                    value={values.about}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.about && errors.about}
                    className="form-input"
                  />
                  {touched.about && errors.about && (
                    <Form.Control.Feedback type="invalid">{errors.about}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </div>

              {/* Contact Details Section */}
              <div className="P-4 mb-4">
                <h6 className="mb-4 section-heading">Contact Details</h6>
                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Mobile Number</Form.Label>
                      <PhoneInput
                        country={'ae'}
                        onlyCountries={['ae', 'in']}
                        value={values.mobileNumber}
                        onChange={(value) => {
                          const cleanedValue = value.replace(/\D/g, '');
                          let isValid = false;
                          let errorMsg = 'Please enter a valid phone number (min 8 digits)';

                          if (cleanedValue.startsWith('91')) {
                            isValid = cleanedValue.length >= 12;
                            errorMsg = 'Please enter a valid Indian phone number (min 10 digits)';
                          } else if (cleanedValue.startsWith('971')) {
                            isValid = cleanedValue.length >= 12;
                            errorMsg = 'Please enter a valid UAE phone number (min 9 digits)';
                          } else {
                            isValid = cleanedValue.length >= 10; // Default for other countries
                          }

                          setFieldValue('mobileNumber', cleanedValue);

                          if (!isValid) {
                            setFieldError('mobileNumber', errorMsg);
                          } else {
                            setFieldError('mobileNumber', undefined);
                            checkUserExists('phone_number', cleanedValue, ROLE_IDS.VENDOR_ADMIN)
                              .then(result => {
                                if (result?.exists) {
                                  setFieldError('mobileNumber', 'This phone number is already registered');
                                }
                              })
                              .catch(error => {
                                console.error('Error checking phone number:', error);
                              });
                          }
                          setFieldTouched('mobileNumber', true, false);
                        }}
                        onBlur={(event) => {
                          // Mark field as touched
                          handleBlur({
                            target: {
                              name: 'mobileNumber'
                            }
                          });

                          // Additional validation on blur
                          const value = values.mobileNumber;
                          if (value) {
                            const isValid = value.length >= 9 && value.length <= 15;
                            if (isValid) {
                              checkUserExists('phone_number', value, ROLE_IDS.VENDOR_ADMIN)
                                .then(result => {
                                  if (result?.exists) {
                                    setFieldError('mobileNumber', 'This phone number is already registered');
                                  }
                                })
                                .catch(error => {
                                  console.error('Error checking phone number:', error);
                                });
                            }
                          }
                        }}
                        inputStyle={{
                          width: '100%',
                          height: '40px',
                          backgroundColor: 'white',
                          borderColor: touched.mobileNumber && errors.mobileNumber ? '#dc3545' : '#E9ECEF'
                        }}
                        inputProps={{
                          name: 'mobileNumber',
                          required: true
                        }}
                      />
                      {touched.mobileNumber && errors.mobileNumber && (
                        <div className="invalid-feedback d-block">{errors.mobileNumber}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="emailAddress"
                        placeholder="Enter email address"
                        value={values.emailAddress}
                        onChange={(e) => {
                          handleChange(e);
                          const value = e.target.value;
                          // Check if email exists only when in valid format
                          if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
                            checkUserExists('email', value, ROLE_IDS.VENDOR_ADMIN).then(result => {
                              if (result?.exists) {
                                setFieldError('emailAddress', 'This email is already registered');
                              }
                            });
                          }
                        }}
                        onBlur={handleBlur}
                        isInvalid={touched.emailAddress && errors.emailAddress}
                        className="form-input"
                      />
                      {touched.emailAddress && errors.emailAddress && (
                        <Form.Control.Feedback type="invalid">{errors.emailAddress}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Additional Details Section */}
              <div className="mb-4">
                <h6 className="mb-4 section-heading">Additional Details</h6>
                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>
                        Consultation Fees
                        {vendorFeeRange.from && vendorFeeRange.to && (
                          <span className="text-muted" style={{ fontSize: '12px', marginLeft: '8px' }}>
                            (Range: {vendorFeeRange.from} - {vendorFeeRange.to} AED)
                          </span>
                        )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="consultationFees"
                        placeholder="Enter consultation fees"
                        value={values.consultationFees}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.consultationFees && errors.consultationFees}
                        className="form-input"
                      />
                      {touched.consultationFees && errors.consultationFees && (
                        <Form.Control.Feedback type="invalid">{errors.consultationFees}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Unique ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="uniqueId"
                        placeholder="Enter uniqueId"
                        value={values.uniqueId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.uniqueId && errors.uniqueId}
                        className="form-input"
                      />
                      {touched.uniqueId && errors.uniqueId && (
                        <Form.Control.Feedback type="invalid">{errors.uniqueId}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>
                        Profile Photo
                      </Form.Label>
                      <div style={{ display: 'flex', alignItems: 'center', borderRadius: '4px', overflow: 'hidden', border: '1px solid #E9ECEF' }}>
                        <div
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#fff',
                            color: '#898989',
                            fontSize: '14px',
                            flex: 1,
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {fileName || 'Upload image'}
                        </div>
                        <label
                          htmlFor="profileImage"
                          style={{
                            backgroundColor: '#C9194A',
                            color: '#fff',
                            padding: '9.5px 12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Upload Image
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          id="profileImage"
                        />
                      </div>
                      <div className='mt-2'>
                        <label className="text-muted small">
                          Max Size: 500 KB<br />
                          Dimensions: 400w x 220h
                        </label>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Form Buttons */}
              <div className="d-flex bg-white rounded p-2 mb-0 gap-3">
                <Button
                  variant="light"
                  onClick={handleNavigation}
                  className="custom-btn btn-large"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="danger"
                  disabled={loading || isSubmitting}
                  className="custom-btn btn-large"
                >
                  {loading ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : needsAvailabilitySetup(values.role) ? 'Next' : 'Submit'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddUsers;